import type { ArtifactDegraded } from '../types.ts';

/**
 * The partial-delivery mark (docs/specs/degraded-runs.md): which competitors could not be
 * assessed, why, and the refund that follows.
 *
 * Brass, not signal. The palette carries the argument here (docs/design-system.md): vermilion is
 * rationed for real gaps in the market, and a run that could not reach a vendor has found no gap
 * — it has a hole in its own survey. Making this hot would say the opposite of what it means, and
 * would put the same colour on "nobody does this" and "we could not find out", which is the one
 * distinction this whole tool exists to preserve.
 */
export function DegradedNotice({
  degraded,
  total,
}: {
  degraded: ArtifactDegraded;
  total: number;
}): JSX.Element {
  const n = degraded.failed_vendors.length;
  return (
    <div className="panel degraded-notice">
      <div className="degraded-head">
        <span className="key k-unknown">partial</span>
        <span className="degraded-lede">
          {n} of {total} competitors could not be assessed. They appear as columns of{' '}
          <em>unknown</em> — that means the research did not find out, not that they lack the
          capability.
        </span>
      </div>
      <ul className="degraded-list">
        {degraded.failed_vendors.map((v) => (
          <li key={v.slug}>
            <span className="degraded-vendor">{v.company}</span>
            <span className="degraded-reason mono">{v.error}</span>
          </li>
        ))}
      </ul>
      {degraded.report_credit === 'refund_due' && (
        <p className="degraded-refund">This run does not count toward your monthly runs.</p>
      )}
    </div>
  );
}
